import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Calendar, Download, RefreshCw, Loader2, FileDown } from 'lucide-react';
import ConsistencyBadge from '../../components/ConsistencyBadge';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { haricleriAyikla } from '../../utils/haricKampanyalar';
import { useMetaReach, mrNew, mrAddRow, sumMetrics, withDedupReach } from '../../utils/metaReachDedup';

const COLUMNS = [
  { key: 'harcama',        label: 'Harcama',        format: 'currency' },
  { key: 'gosterim',       label: 'Gösterim',       format: 'number'   },
  { key: 'etkilesim',      label: 'Etkileşim',      format: 'number'   },
  { key: 'erisim',         label: 'Erişim',          format: 'number'   },
  { key: 'izlenme',        label: 'İzlenme',         format: 'number'   },
  { key: 'vcr',            label: 'VCR',             format: 'percent'  },
  { key: 'tiklama',        label: 'Tıklama',         format: 'number'   },
  { key: 'tekilKullanici', label: 'Tekil Kullanıcı', format: 'number'   },
  { key: 'trafik',         label: 'Trafik',          format: 'number'   },
  { key: 'storeVisit',     label: 'Store Visit',     format: 'number'   },
  { key: 'gelir',          label: 'Gelir',           format: 'currency' },
  { key: 'satis',          label: 'Satış',           format: 'number'   },
];

const TABLE_DATA = [
  {
    platform: 'Google Ads',
    rows: [
      { name: 'Youtube' },
      { name: 'Search' },
      { name: 'P-max Conv.' },
      { name: 'P-Max Branding' },
      { name: 'Demand Gen' },
      { name: 'Display / GDN' },
      { name: 'P-max Local' },
    ],
  },
  {
    platform: 'META',
    rows: [
      { name: 'Dönüşüm' },
      { name: 'Trafik' },
      { name: 'Erişim' },
      { name: 'Bilinirlik' },
      { name: 'Etkileşim' },
      { name: 'Thruplay' },
      { name: 'Local - Store Visit' },
    ],
  },
  {
    platform: 'Pinterest',
    rows: [{ name: 'Pinterest / Performans' }, { name: 'Pinterest / Branding' }],
  },
  {
    platform: 'TikTok',
    rows: [{ name: 'TikTok' }],
  },
  {
    platform: 'Remarketing',
    rows: [{ name: 'Criteo' }, { name: 'RTB' }],
  },
  {
    platform: 'Media Satın Alma',
    rows: [{ name: 'Display' }, { name: 'Native' }, { name: 'Video' }],
  },
];

const SUMMARY_ROWS = [
  { label: 'Youtube',           sources: [{ platform: 'Google Ads', rows: ['Youtube'] }] },
  { label: 'Google',            sources: [{ platform: 'Google Ads', rows: ['Search', 'P-max Conv.', 'P-Max Branding', 'Demand Gen', 'Display / GDN', 'P-max Local'] }] },
  { label: 'Meta',              sources: [{ platform: 'META', rows: ['Dönüşüm', 'Trafik', 'Erişim', 'Bilinirlik', 'Etkileşim', 'Thruplay', 'Local - Store Visit'] }] },
  { label: 'Pinterest',         sources: [{ platform: 'Pinterest', rows: ['Pinterest / Performans', 'Pinterest / Branding'] }] },
  { label: 'TikTok',            sources: [{ platform: 'TikTok', rows: ['TikTok'] }] },
  { label: 'Remarketing',       sources: [{ platform: 'Remarketing', rows: ['Criteo', 'RTB'] }] },
  { label: 'Medya Satın Alma',  sources: [{ platform: 'Media Satın Alma', rows: ['Display', 'Native', 'Video'] }] },
];

const API = 'https://api.glowytics.com';
const EXCLUDE_FROM_MANUAL_BRANDING = [
  'google_ads', 'meta_ads', 'google_ads_performance', 'meta_ads_performance',
  'google_ads_branding', 'meta_ads_branding',
  'analytics_revenue', 'analytics_revenue_performance', 'analytics_revenue_branding',
];

function daysOverlap(startA, endA, startB, endB) {
  const start = startA > startB ? startA : startB;
  const end = endA < endB ? endA : endB;
  if (end < start) return 0;
  return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
}

function listYearMonthInRange(startDate, endDate) {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const periods = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    periods.push({ year: cursor.getFullYear(), month: cursor.getMonth() + 1 });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return periods;
}

const normalizeLabel = (v) => (v || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

function buildCustomBrandingFromPlans(plansByYear, rangeStart, rangeEnd) {
  const start = new Date(rangeStart);
  const end = new Date(rangeEnd);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  const byKey = new Map();
  const addToBucket = (r, year, bucketKey, bucketLabel) => {
    if (!byKey.has(bucketKey)) byKey.set(bucketKey, { key: bucketKey, label: bucketLabel, harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0 });
    const bucket = byKey.get(bucketKey);
    const mv = r.metricsValues || {};
    for (let month = 1; month <= 12; month += 1) {
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);
      const overlap = daysOverlap(start, end, monthStart, monthEnd);
      if (overlap <= 0) continue;
      const daysInMonth = new Date(year, month, 0).getDate();
      const ratio = overlap / daysInMonth;
      bucket.harcama += (Number(r.values?.[month] ?? r.values?.[String(month)] ?? 0) || 0) * ratio;
      const m = mv[month] ?? mv[String(month)] ?? {};
      bucket.gosterim += (Number(m.gosterim) || 0) * ratio;
      bucket.erisim += (Number(m.erisim) || 0) * ratio;
      bucket.izlenme += (Number(m.izlenme) || 0) * ratio;
      bucket.trafik += (Number(m.trafik) || 0) * ratio;
    }
  };
  const excludeLabels = ['pinterest', 'medya satınalma', 'medya satinalma'];
  plansByYear.forEach(({ year, rows }) => {
    (rows || []).forEach((r) => {
      if ((r.metricType || '').toLowerCase() !== 'spend') return;
      const rowKey = (r.key || '').toString();
      if (EXCLUDE_FROM_MANUAL_BRANDING.includes(rowKey)) return;
      const labelNorm = normalizeLabel(r.label);
      if (excludeLabels.some(l => labelNorm.includes(l))) return;
      // 🔴 Planlayıcıda satır BRANDING mi PERFORMANS mı, anahtarın önekinde duruyor
      // (`custom_branding_*` / `custom_performance_*`). Eskiden yalnız "elle
      // eklenmiş mi" diye bakılıyordu, o yüzden PERFORMANS satırları da branding
      // tablosuna giriyordu: Yataş Bedding'de Bing/Yandex/Cimri/Akakçe — Ağustos
      // 2026'da 168.022 ₺ performans harcaması branding sayılıyor, üstelik Yandex
      // ve Bing hem branding hem performans satırıyla İKİ KEZ listeleniyordu.
      // `bucket` sütunu bu satırlarda BOŞ, o yüzden ay bazlı eleme de yakalamıyor.
      if (rowKey.includes('_performance')) return;
      const isBrandingKey = rowKey.includes('_branding');
      const isCustom = r.isSystem === false;
      if (!isBrandingKey && !isCustom) return;
      const bucketKey = rowKey || `custom_${labelNorm.replace(/\s+/g, '_')}`;
      const bucketLabel = (r.label || bucketKey).trim() || 'Manuel';
      addToBucket(r, year, bucketKey, bucketLabel);
    });
  });
  return Array.from(byKey.values());
}

/* ── Classify & Aggregate (same logic as CustomReport1) ─────────── */

function classifyGoogleAdsCampaign(campaignType, campaignName) {
  const type = (campaignType || '').toLowerCase();
  const name = (campaignName || '').toLowerCase();
  if (type.includes('performance_max') || type === 'pmax') {
    if (name.includes('branding')) return 'P-Max Branding';
    if (name.includes('lokal'))    return 'P-max Local';
    return 'P-max Conv.';
  }
  if (type.includes('search'))     return 'Search';
  if (type.includes('video'))      return 'Youtube';
  if (type.includes('demand_gen')) return 'Demand Gen';
  if (type.includes('display'))    return 'Display / GDN';
  if (type.includes('shopping'))   return 'Search';
  return null;
}

function aggregateGoogleAdsData(rawData) {
  const GA_ROWS = ['Youtube', 'Search', 'P-max Conv.', 'P-Max Branding', 'Demand Gen', 'Display / GDN', 'P-max Local'];
  const buckets = {};
  GA_ROWS.forEach(r => { buckets[r] = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 }; });
  (rawData || []).forEach(row => {
    const bucket = classifyGoogleAdsCampaign(row.campaignType, row.campaignName);
    if (!bucket || !buckets[bucket]) return;
    buckets[bucket].harcama  += Number(row.googleAdsCost) || 0;
    buckets[bucket].gosterim += Number(row.googleAdsImpression) || 0;
    buckets[bucket].tiklama  += Number(row.googleAdsClicks) || 0;
    buckets[bucket].trafik   += Number(row.sessions) || 0;
    buckets[bucket].gelir    += Number(row.googleAdsRevenue) || 0;
    buckets[bucket].satis    += Number(row.googleAdsTransaction) || 0;
  });
  const result = {};
  GA_ROWS.forEach(r => {
    if (Object.values(buckets[r]).some(v => v > 0)) result[r] = buckets[r];
  });
  return result;
}

function classifyMetaCampaign(objective, campaignName) {
  const name = (campaignName || '').toLowerCase().replace(/[\s\-_]/g, '');
  const nameRaw = (campaignName || '').toLowerCase();
  if (name.includes('videoview') || name.includes('thruplay')) return 'Thruplay';
  if (name.includes('storevisit') || name.includes('localstoreawareness')) return 'Local - Store Visit';
  if (nameRaw.includes('instagram gönderi') || nameRaw.includes('instagram post')) return 'Etkileşim';
  if (name.includes('postengagement') || (name.includes('engagement') && !name.includes('video'))) return 'Etkileşim';
  if (name.includes('reach') || name.includes('brandawareness')) return 'Bilinirlik';
  if (name.includes('traffic') || name.includes('landingpageview') || name.includes('profilevisit')) return 'Trafik';
  const obj = (objective || '').toUpperCase().trim();
  if (['OUTCOME_SALES', 'CONVERSIONS'].includes(obj))                         return 'Dönüşüm';
  if (['OUTCOME_TRAFFIC', 'TRAFFIC', 'LINK_CLICKS'].includes(obj))            return 'Trafik';
  if (['REACH'].includes(obj))                                                 return 'Erişim';
  if (['OUTCOME_AWARENESS', 'AWARENESS', 'BRAND_AWARENESS'].includes(obj))     return 'Bilinirlik';
  if (['OUTCOME_ENGAGEMENT', 'ENGAGEMENT', 'POST_ENGAGEMENT'].includes(obj))   return 'Etkileşim';
  if (['VIDEO_VIEWS', 'THRUPLAY'].includes(obj))                               return 'Thruplay';
  if (['STORE_TRAFFIC', 'LOCAL_AWARENESS', 'STORE_VISITS'].includes(obj))      return 'Local - Store Visit';
  return null;
}

function aggregateMetaData(rawData) {
  const META_ROWS = ['Dönüşüm', 'Trafik', 'Erişim', 'Bilinirlik', 'Etkileşim', 'Thruplay', 'Local - Store Visit'];
  const buckets = {};
  META_ROWS.forEach(r => { buckets[r] = { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, _mr: mrNew() }; });
  (rawData || []).forEach(row => {
    const objective = row.campaign_objective || row.campaignObjective;
    const cName = row.campaign_name || row.campaignName || 'Unknown';
    const bucket = classifyMetaCampaign(objective, cName);
    if (!bucket || !buckets[bucket]) return;
    const reach = Number(row.reach) || 0;
    const view = Number(row.view || row.trueplay) || 0;
    buckets[bucket].harcama        += Number(row.spend) || 0;
    buckets[bucket].gosterim       += Number(row.impressions) || 0;
    buckets[bucket].tiklama        += Number(row.clicks) || 0;
    buckets[bucket].etkilesim      += Number(row.actions) || 0;
    buckets[bucket].erisim         += reach;
    // Erişim toplanamaz — kampanya kimliği tutulur, tekil değer Meta'dan sorulur
    mrAddRow(buckets[bucket]._mr, row, { level: 'campaign' });
    buckets[bucket].izlenme        += view;
    buckets[bucket].trafik         += Number(row.ga4_sessions || row.ga4Sessions) || 0;
    buckets[bucket].tekilKullanici += Number(row.ga4_users || row.ga4Users) || 0;
    buckets[bucket].gelir          += Number(row.conversion_value || row.conversionValue) || 0;
    buckets[bucket].satis          += Number(row.conversions) || 0;
  });
  const result = {};
  META_ROWS.forEach(r => {
    if (Object.values(buckets[r]).some(v => v > 0)) result[r] = buckets[r];
  });
  return result;
}

/* ── Formatting ─────────────────────────────────────────────────── */

const formatValue = (value, format) => {
  if (value === null || value === undefined || value === '' || value === 0) return '';
  switch (format) {
    case 'currency': return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    case 'number':   return new Intl.NumberFormat('tr-TR').format(Math.round(value));
    case 'percent':  return `%${(value * 100).toFixed(1)}`;
    default:         return value;
  }
};

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getLastCompletedMonthRange = () => {
  const now = new Date();
  return {
    startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
    endDate: new Date(now.getFullYear(), now.getMonth(), 0),
  };
};

/* ── Styles ─────────────────────────────────────────────────────── */

const S = {
  headerCell: 'px-2 py-[6px] text-[10px] font-bold text-white uppercase tracking-wide whitespace-nowrap border border-slate-500 bg-slate-700 text-center',
  platformCell: 'px-2 py-[5px] text-[10px] font-bold text-white uppercase tracking-wide whitespace-nowrap border border-slate-500 bg-slate-600',
  rowLabel: 'px-2 py-[5px] text-[10.5px] text-slate-700 font-medium whitespace-nowrap border border-slate-200 bg-white',
  dataCell: 'px-2 py-[5px] text-[10px] text-right tabular-nums text-slate-600 whitespace-nowrap border border-slate-200 bg-white',
  totalLabel: 'px-2 py-[5px] text-[10px] font-bold text-white uppercase tracking-wide whitespace-nowrap border border-slate-500 bg-slate-800',
  totalCell: 'px-2 py-[5px] text-[10px] font-bold text-right tabular-nums text-white whitespace-nowrap border border-slate-500 bg-slate-800',
};

/* ── Component ──────────────────────────────────────────────────── */

export default function CustomReport5() {
  const [startDate, setStartDate] = useState(() => getLastCompletedMonthRange().startDate);
  const [endDate, setEndDate] = useState(() => getLastCompletedMonthRange().endDate);
  const [pendingStartDate, setPendingStartDate] = useState(() => getLastCompletedMonthRange().startDate);
  const [pendingEndDate, setPendingEndDate] = useState(() => getLastCompletedMonthRange().endDate);
  const [reportDataRaw, setReportData] = useState({});
  // Meta erişimi: DB toplamı yerine Meta API'den tekil değer (utils/metaReachDedup)
  const reachOf = useMetaReach('https://api.glowytics.com', localStorage.getItem('currentBrandId'), startDate, endDate);
  const reportData = useMemo(() => withDedupReach(reportDataRaw, reachOf), [reportDataRaw, reachOf]);
  const [loading, setLoading] = useState(false);
  const [customBrandingRows, setCustomBrandingRows] = useState([]);
  const pdfRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = useCallback(async () => {
    if (exporting || !pdfRef.current) return;
    setExporting(true);
    try {
      const el = pdfRef.current;
      const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 1400, letterRendering: true, allowTaint: true });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const imgW = usableW;
      const imgH = (canvas.height * usableW) / canvas.width;
      let y = 0;
      let page = 0;
      while (y < imgH) {
        if (page > 0) pdf.addPage();
        const srcY = (y / imgH) * canvas.height;
        const srcH = Math.min((usableH / imgH) * canvas.height, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        sliceCanvas.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
        const sliceH = (srcH * usableW) / canvas.width;
        pdf.addImage(sliceData, 'JPEG', margin, margin, imgW, sliceH);
        y += usableH;
        page++;
      }
      pdf.save('Mecra_Kirilimli_Rapor.pdf');
    } catch (err) { console.error('PDF export failed:', err); }
    finally { setExporting(false); }
  }, [exporting]);

  const onDateRangeChange = useCallback((range) => {
    const [s, e] = range || [];
    setPendingStartDate(s || null);
    setPendingEndDate(e || null);
    if (s && e) {
      setStartDate(s);
      setEndDate(e);
    }
  }, []);

  const fetchGoogleAdsData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ dateRange: 'custom', startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      const res = await fetch(`https://api.glowytics.com/api/google-ads/campaign-types-all?${params}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Google Ads fetch failed');
      const json = await res.json();
      const googleAggregated = aggregateGoogleAdsData(json.rawData);

      let videoExtra = {};
      try {
        const vRes = await fetch(`https://api.glowytics.com/api/google-ads/video-performance-summary?${params}`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        });
        if (vRes.ok) {
          const vRows = await vRes.json();
          const arr = Array.isArray(vRows) ? vRows : vRows.data || vRows.rawData || [];
          // İzlenmeyi kampanya TÜRÜNE göre satırlara dağıt. Endpoint 2026-07-28'den
          // beri PMax / Demand Gen / Display izlenmelerini de döndürüyor; hepsi tek
          // 'Youtube' kovasına yazılırsa PMax izlenmesi YouTube etiketiyle görünür.
          // Sayfanın kendi sınıflandırıcısı kullanılıyor → izlenme, harcamayla aynı
          // satıra düşüyor. (Yalnızca izlenme/vcr yazılır, harcama/gösterim değil —
          // onlar zaten aggregateGoogleAdsData'dan geliyor, çift sayım yok.)
          const byRow = {};
          arr.forEach(r => {
            const vv = Number(r.video_views) || 0;
            const imp = Number(r.impressions) || 0;
            // Bumper/non-skippable/CTV (view=0) → impression'ı izlenme say (backend effective_views).
            const ev = r.effective_views != null ? Number(r.effective_views) : (vv > 0 ? vv : imp);
            if (!ev) return;
            // campaign_type dönmeyen eski cevaplarda davranış aynı kalsın → Youtube.
            const rowName = classifyGoogleAdsCampaign(r.campaign_type || 'VIDEO', r.campaign_name) || 'Youtube';
            const b = byRow[rowName] || (byRow[rowName] = { views: 0, imp: 0 });
            b.views += ev;
            b.imp += imp;
          });
          videoExtra = byRow;
        }
      } catch {}

      Object.entries(videoExtra).forEach(([rowName, b]) => {
        if (!googleAggregated[rowName]) return;
        googleAggregated[rowName] = {
          ...googleAggregated[rowName],
          izlenme: b.views,
          vcr: b.imp > 0 ? b.views / b.imp : 0,
        };
      });

      setReportData(prev => ({ ...prev, 'Google Ads': googleAggregated }));
    } catch (err) { console.error('Google Ads fetch error:', err); }
  }, [startDate, endDate]);

  const fetchMetaData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ dateRange: 'custom', startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      const res = await fetch(`https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?${params}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Meta fetch failed');
      const json = await res.json();
      // Yunanistan gibi `raporlardanCikar` kampanyaları hiçbir raporda görünmez (Yataş, 6 Eki 2026).
      const metaRaw = haricleriAyikla(Array.isArray(json) ? json : json.data || json.rawData || [], brandId);
      setReportData(prev => ({ ...prev, 'META': aggregateMetaData(metaRaw) }));
    } catch (err) { console.error('Meta fetch error:', err); }
  }, [startDate, endDate]);

  const fetchCriteoData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      const res = await fetch(`https://api.glowytics.com/api/criteo/db/campaign-summary?${params}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;
      const totals = {
        harcama: json.totals?.cost || 0,
        gosterim: json.totals?.impressions || 0,
        tiklama: json.totals?.clicks || 0,
      };
      setReportData(prev => ({
        ...prev,
        'Remarketing': { ...(prev['Remarketing'] || {}), 'Criteo': totals },
      }));
    } catch (err) { console.error('Criteo fetch error:', err); }
  }, [startDate, endDate]);

  const fetchTikTokData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      const res = await fetch(`https://api.glowytics.com/api/tiktok-ads/unified-campaign-performance?${params}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const rows = await res.json();
      if (!Array.isArray(rows)) return;
      const totals = rows.reduce((t, r) => ({
        harcama: t.harcama + (r.spend || 0),
        gosterim: t.gosterim + (r.impressions || 0),
        tiklama: t.tiklama + (r.clicks || 0),
        erisim: t.erisim + (r.reach || 0),
        // TikTok Ads Manager'daki "Video İzlenme" = video_play_actions
        // (video_views_p100 %100 tamamlamadır).
        izlenme: t.izlenme + (r.video_play_actions || 0),
        etkilesim: t.etkilesim + (r.likes || 0) + (r.comments || 0) + (r.shares || 0),
        trafik: t.trafik + (r.ga4_sessions || 0),
        gelir: t.gelir + (r.ga4_total_revenue || 0),
        satis: t.satis + (r.ga4_purchases || 0),
      }), { harcama: 0, gosterim: 0, tiklama: 0, erisim: 0, izlenme: 0, etkilesim: 0, trafik: 0, gelir: 0, satis: 0 });
      setReportData(prev => ({
        ...prev,
        'TikTok': { ...(prev['TikTok'] || {}), 'TikTok': totals },
      }));
    } catch (err) { console.error('TikTok fetch error:', err); }
  }, [startDate, endDate]);

  const fetchRtbHouseData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ dayFrom: fmtDate(startDate), dayTo: fmtDate(endDate) });
      const res = await fetch(`https://api.glowytics.com/api/rtbhouse/stats/by-campaign?${params}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;
      const campaigns = json.rows || [];
      const totals = campaigns.reduce((t, r) => ({
        harcama: t.harcama + (r.campaignCost || 0),
        gosterim: t.gosterim + (r.impsCount || 0),
        tiklama: t.tiklama + (r.clicksCount || 0),
        trafik: t.trafik + (r.ga4Sessions || 0),
        gelir: t.gelir + (r.ga4Revenue || 0),
        satis: t.satis + (r.ga4Purchases || 0),
      }), { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 });
      setReportData(prev => ({
        ...prev,
        'Remarketing': { ...(prev['Remarketing'] || {}), 'RTB': totals },
      }));
    } catch (err) { console.error('RTB House fetch error:', err); }
  }, [startDate, endDate]);

  const fetchPinterestData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      const res = await fetch(`https://api.glowytics.com/api/pinterest/db/stats?${params}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      const buckets = {
        'Pinterest / Performans': { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, gelir: 0, satis: 0 },
        'Pinterest / Branding': { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, gelir: 0, satis: 0 },
      };
      (json.campaigns || []).forEach(c => {
        const name = (c.name || '').toLowerCase();
        const bucket = (name.includes('brand') || name.includes('awareness') || name.includes('reach') || name.includes('traffic'))
          ? 'Pinterest / Branding'
          : 'Pinterest / Performans';
        buckets[bucket].harcama += Number(c.spend) || 0;
        buckets[bucket].gosterim += Number(c.impressions) || 0;
        buckets[bucket].erisim += Number(c.reach) || 0;
        buckets[bucket].izlenme += Number(c.video_views) || 0;
        buckets[bucket].tiklama += Number(c.clicks) || 0;
        buckets[bucket].gelir += Number(c.conversions_value) || 0;
        buckets[bucket].satis += Number(c.conversions) || 0;
      });
      const result = {};
      Object.entries(buckets).forEach(([key, val]) => {
        if (val.harcama > 0 || val.gosterim > 0 || val.tiklama > 0 || val.erisim > 0 || val.izlenme > 0) result[key] = val;
      });
      setReportData(prev => ({ ...prev, 'Pinterest': result }));
    } catch (err) { console.error('Pinterest fetch error:', err); }
  }, [startDate, endDate]);

  const fetchMediaBuyingData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const year = endDate.getFullYear();
      const month = endDate.getMonth() + 1;
      const res = await fetch(
        `https://api.glowytics.com/api/brands/${brandId}/media-buying-format?year=${year}&month=${month}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );
      if (!res.ok) return;
      const json = await res.json();
      if (!json.success || !Array.isArray(json.sections)) return;
      // Statik Display/Native/Video YERİNE medya satın almada ne girildiyse onu göster.
      // Kanonik fallback: Dağılım yoksa Mecra'ya düş (toplam aynı para, kırılım yayıncıya iner).
      const isMecra = (t) => (t || '').toLowerCase().includes('mecra');
      const mbSection =
        json.sections.find(s => s.title === 'Medya Satın Alma Dağılımı') ||
        json.sections.find(s => isMecra(s.title)) ||
        json.sections.find(s => Array.isArray(s.rows) && s.rows.length) ||
        json.sections[0];
      if (!mbSection || !Array.isArray(mbSection.rows)) return;
      const parseTR = (str) => {
        if (!str || str === '-' || str === '–') return 0;
        const cleaned = String(str).replace(/[₺\s]/g, '').replace(/\./g, '').replace(/,/g, '.');
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
      };
      const result = {};
      mbSection.rows.forEach(r => {
        const channel = (r.channel || '').trim();
        if (!channel) return;
        if (!result[channel]) result[channel] = { harcama: 0, gosterim: 0, tiklama: 0, izlenme: 0 };
        result[channel].harcama += parseTR(r.budget);
        result[channel].gosterim += parseTR(r.impressions);
        result[channel].tiklama += parseTR(r.clicks);
        result[channel].izlenme += parseTR(r.views);
      });
      setReportData(prev => ({ ...prev, 'Media Satın Alma': result }));
    } catch (err) { console.error('Media Buying fetch error:', err); }
  }, [endDate]);

  const fetchCustomBrandingFromPlans = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const periods = listYearMonthInRange(startDate, endDate);
      const years = [...new Set(periods.map(p => p.year))];
      const responses = await Promise.all(
        years.map(year =>
          fetch(`${API}/api/brands/${brandId}/monthly-target-plans?year=${year}`, {
            credentials: 'include',
            headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
          }).then(r => r.ok ? r.json() : { rows: [] })
        )
      );
      const plansByYear = years.map((year, i) => ({ year, rows: responses[i]?.rows || [] }));
      const rows = buildCustomBrandingFromPlans(plansByYear, startDate, endDate);
      setCustomBrandingRows(rows);
      const customData = {};
      rows.forEach(r => {
        const platformLabel = r.label;
        const rowName = `${platformLabel} / Branding`;
        customData[platformLabel] = { [rowName]: { harcama: Math.round(r.harcama), gosterim: Math.round(r.gosterim), erisim: Math.round(r.erisim), izlenme: Math.round(r.izlenme), trafik: Math.round(r.trafik) } };
      });
      setReportData(prev => ({ ...prev, ...customData }));
    } catch (err) { console.error('Custom branding fetch error:', err); }
  }, [startDate, endDate]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchGoogleAdsData(),
      fetchMetaData(),
      fetchCriteoData(),
      fetchTikTokData(),
      fetchRtbHouseData(),
      fetchPinterestData(),
      fetchMediaBuyingData(),
      fetchCustomBrandingFromPlans(),
    ]);
    setLoading(false);
  }, [fetchGoogleAdsData, fetchMetaData, fetchCriteoData, fetchTikTokData, fetchRtbHouseData, fetchPinterestData, fetchMediaBuyingData, fetchCustomBrandingFromPlans]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  // Medya satın alma satırları artık girilen mecralara göre dinamik (statik Display/Native/Video değil).
  const mbChannelNames = useMemo(
    () => Object.keys(reportData['Media Satın Alma'] || {}),
    [reportData]
  );

  const computedTableData = useMemo(() => {
    const customSections = customBrandingRows.map(r => ({
      platform: r.label,
      rows: [{ name: `${r.label} / Branding` }],
    }));
    const base = TABLE_DATA.map(g =>
      g.platform === 'Media Satın Alma' && mbChannelNames.length
        ? { ...g, rows: mbChannelNames.map(name => ({ name })) }
        : g
    );
    return [...base, ...customSections];
  }, [customBrandingRows, mbChannelNames]);

  const computedSummaryRows = useMemo(() => {
    const customSummary = customBrandingRows.map(r => ({
      label: r.label,
      sources: [{ platform: r.label, rows: [`${r.label} / Branding`] }],
    }));
    const base = SUMMARY_ROWS.map(sr =>
      sr.label === 'Medya Satın Alma' && mbChannelNames.length
        ? { ...sr, sources: [{ platform: 'Media Satın Alma', rows: mbChannelNames }] }
        : sr
    );
    return [...base, ...customSummary];
  }, [customBrandingRows, mbChannelNames]);

  /* ── Compute totals per platform ─────────────────────────── */

  const platformTotals = useMemo(() => {
    const result = {};
    computedTableData.forEach(g => {
      const pd = reportData[g.platform] || {};
      // Erişim: Meta satırlarının kampanya kümeleri birleştirilip tekil sorulur
      result[g.platform] = sumMetrics(g.rows.map(r => pd[r.name]), COLUMNS, reachOf);
    });
    return result;
  }, [reportData, computedTableData, reachOf]);

  const grandTotal = useMemo(
    () => sumMetrics(Object.values(platformTotals), COLUMNS, reachOf),
    [platformTotals, reachOf]
  );

  const summaryData = useMemo(() => {
    const rows = computedSummaryRows.map(sr => {
      const items = [];
      sr.sources.forEach(src => {
        const pd = reportData[src.platform] || {};
        src.rows.forEach(r => items.push(pd[r]));
      });
      return { label: sr.label, metrics: sumMetrics(items, COLUMNS, reachOf) };
    });
    const total = sumMetrics(rows.map(r => r.metrics), COLUMNS, reachOf);
    return { rows, total };
  }, [reportData, computedSummaryRows, reachOf]);

  /* ── Count total rows for platform rowSpan ───────────────── */

  const platformRowSpan = (group) => group.rows.length;

  return (
    <div className="min-h-screen" ref={pdfRef}>
      <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800 tracking-tight">Mecra Kırılımlı Rapor Formatı</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">Tüm reklam mecralarının kırılımlı performans tablosu</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ConsistencyBadge startDate={startDate} endDate={endDate} />
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm">
            <Calendar size={12} className="text-slate-400" />
            <DatePicker
              selectsRange
              startDate={pendingStartDate}
              endDate={pendingEndDate}
              onChange={onDateRangeChange}
              dateFormat="dd MMM yyyy"
              className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[170px] cursor-pointer"
            />
          </div>
          <button onClick={fetchAllData} disabled={loading} className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Yenile
          </button>
          <button className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50 shadow-sm transition-colors">
            <Download size={11} /> Excel
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-md px-2.5 py-1 text-[11px] font-medium shadow-sm transition-colors"
          >
            {exporting ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
            {exporting ? 'PDF...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* ── Özet Tablosu ──────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden relative mb-8">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[1100px]">
            <thead>
              <tr>
                <th className={S.headerCell} style={{ minWidth: 200 }}>MECRA</th>
                {COLUMNS.map(col => <th key={col.key} className={S.headerCell}>{col.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {summaryData.rows.map(row => (
                <tr key={row.label}>
                  <td className={`${S.platformCell} text-[10.5px]`}>{row.label}</td>
                  {COLUMNS.map(col => (
                    <td key={col.key} className={S.dataCell}>{formatValue(row.metrics[col.key], col.format)}</td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className={S.totalLabel}>TOTAL</td>
                {COLUMNS.map(col => <td key={col.key} className={S.totalCell}>{formatValue(summaryData.total[col.key], col.format)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-30 rounded-lg">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-md shadow-md border border-slate-200">
              <Loader2 size={14} className="text-slate-500 animate-spin" />
              <span className="text-[11px] text-slate-500">Veriler yükleniyor…</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Detay Tablosu ─────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden relative">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[1100px]">
            <thead>
              <tr>
                <th colSpan={2} className={S.headerCell} style={{ minWidth: 200 }}>MECRA</th>
                {COLUMNS.map(col => <th key={col.key} className={S.headerCell}>{col.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {computedTableData.map(group => {
                const pd = reportData[group.platform] || {};
                const span = platformRowSpan(group);
                return group.rows.map((row, idx) => (
                  <tr key={`${group.platform}-${row.name}`}>
                    {idx === 0 && (
                      <td rowSpan={span} className={S.platformCell}>{group.platform}</td>
                    )}
                    <td className={S.rowLabel}>{row.name}</td>
                    {COLUMNS.map(col => (
                      <td key={col.key} className={S.dataCell}>
                        {formatValue(pd[row.name]?.[col.key], col.format)}
                      </td>
                    ))}
                  </tr>
                ));
              })}
              <tr>
                <td colSpan={2} className={S.totalLabel}>TOTAL</td>
                {COLUMNS.map(col => <td key={col.key} className={S.totalCell}>{formatValue(grandTotal[col.key], col.format)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
